import './ApplicantInfo.css';

import { formatISO } from 'date-fns';
import React, { useEffect, useState } from 'react';
import PhoneInput from 'react-phone-input-2';

import Address from '../../../../components/Address/Address';
import { AddressData } from '../../../../components/Address/AddressTypes';
import {
  AdditionalInsuredInfoListForApplication,
  BusinessInfo,
  useUpdateApplicationAnswersAndQuoteMutation
} from '../../../../generated/graphql';
import { NewQuoteForApp } from '../../Quote';

interface ApplicantInfoProps {
  businessInfo: BusinessInfo;
  applicationGroupId: string;
  country: string;
  province: string;
  applyNewQuoteIds: (newQuoteIds: NewQuoteForApp[]) => void;
  additionalInsuredInfoListByAppId?: Record<
    string,
    AdditionalInsuredInfoListForApplication['additionalInsuredInfoList']
  >;
  refetchBusinessInfo?: () => void;
}

type ApplicantInfoForm = {
  companyName: string;
  dbaName: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  mailingAddress: AddressData;
};

const EMPTY_ADDRESS: AddressData = {
  street: '',
  city: '',
  province: '',
  postalCode: '',
  address2: ''
};

type ApplicantAddress = NonNullable<BusinessInfo['mailingAddress']>;

const toAddressData = (address?: ApplicantAddress | null): AddressData =>
  address
    ? {
        street: address.street,
        city: address.city,
        province: address.province,
        postalCode: address.postalCode,
        address2: address.address2 ?? ''
      }
    : { ...EMPTY_ADDRESS };

const toAddressLines = (
  address?: ApplicantAddress | AddressData | null
): string[] => {
  if (!address || !address.street) return ['—'];
  // street/address2 come back un-folded from the API; show the unit inline in
  // the read-only view (edit mode renders address2 as its own field).
  const line1 = address.address2
    ? `${address.street}, ${address.address2}`
    : address.street;
  return [line1, `${address.city}, ${address.province} ${address.postalCode}`];
};

const toForm = (businessInfo: BusinessInfo): ApplicantInfoForm => ({
  companyName: businessInfo.companyName ?? '',
  dbaName: businessInfo.dbaName ?? '',
  // First/last name stay separate fields end to end (matching the application
  // journey's two questions) so names round-trip without any parsing.
  firstName: businessInfo.firstName ?? '',
  lastName: businessInfo.lastName ?? '',
  email: businessInfo.email ?? '',
  phoneNumber: businessInfo.phoneNumber ?? '',
  mailingAddress: toAddressData(businessInfo.mailingAddress)
});

const addressesEqual = (a: AddressData, b: AddressData): boolean =>
  a.street === b.street &&
  a.city === b.city &&
  a.province === b.province &&
  a.postalCode === b.postalCode &&
  (a.address2 ?? '') === (b.address2 ?? '');

const formatPhoneNumber = (raw: string): string => {
  const digits = raw.replace(/\D/g, '');
  const tenDigits =
    digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (tenDigits.length !== 10) return raw;
  return `(${tenDigits.slice(0, 3)}) ${tenDigits.slice(3, 6)}-${tenDigits.slice(
    6
  )}`;
};

const Row: React.FC<{ label: string; lines: string[]; testId: string }> = ({
  label,
  lines,
  testId
}) => (
  <div className="applicant-info-row" data-testid={testId}>
    <span className="applicant-info-label">{label}</span>
    <span className="applicant-info-value">
      {lines.map((line, i) => (
        <span key={i} className="block">
          {line}
        </span>
      ))}
    </span>
  </div>
);

const ApplicantInfo: React.FC<ApplicantInfoProps> = ({
  businessInfo,
  applicationGroupId,
  country,
  province,
  applyNewQuoteIds,
  additionalInsuredInfoListByAppId,
  refetchBusinessInfo
}) => {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  // `saved` is what is currently persisted/displayed; `draft` is the in-edit copy.
  const [saved, setSaved] = useState<ApplicantInfoForm>(() =>
    toForm(businessInfo)
  );
  const [draft, setDraft] = useState<ApplicantInfoForm>(saved);
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [invalidPhone, setInvalidPhone] = useState(false);
  const [invalidCompanyName, setInvalidCompanyName] = useState(false);
  const [invalidFirstName, setInvalidFirstName] = useState(false);
  const [invalidLastName, setInvalidLastName] = useState(false);
  const [saveError, setSaveError] = useState(false);

  const [
    updateApplicationAnswersAndQuote,
    { loading }
  ] = useUpdateApplicationAnswersAndQuoteMutation();

  useEffect(() => {
    const next = toForm(businessInfo);
    setSaved(next);
    if (!editing) setDraft(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessInfo]);

  const addressCountry = country.toLowerCase().includes('canada') ? 'CA' : 'US';

  const startEdit = () => {
    setDraft(saved);
    setInvalidEmail(false);
    setInvalidPhone(false);
    setInvalidCompanyName(false);
    setInvalidFirstName(false);
    setInvalidLastName(false);
    setSaveError(false);
    setExpanded(true);
    setEditing(true);
  };

  const cancel = () => {
    setDraft(saved);
    setInvalidEmail(false);
    setInvalidPhone(false);
    setInvalidCompanyName(false);
    setInvalidFirstName(false);
    setInvalidLastName(false);
    setSaveError(false);
    setEditing(false);
  };

  const patch = (changes: Partial<ApplicantInfoForm>) =>
    setDraft((prev) => ({ ...prev, ...changes }));

  const save = async () => {
    const phoneOk = /^1\d{10}$/.test(draft.phoneNumber);
    const emailOk = draft.email.trim().length > 0;
    // Required: the resolver stores newCompanyName verbatim (no fallback to the
    // current value like first/last name), so an empty string would wipe it.
    const companyName = draft.companyName.trim();
    const first = draft.firstName.trim();
    const last = draft.lastName.trim();
    setInvalidPhone(!phoneOk);
    setInvalidEmail(!emailOk);
    setInvalidCompanyName(companyName.length === 0);
    setInvalidFirstName(first.length === 0);
    setInvalidLastName(last.length === 0);
    if (
      !phoneOk ||
      !emailOk ||
      companyName.length === 0 ||
      first.length === 0 ||
      last.length === 0
    ) {
      return;
    }
    setSaveError(false);

    const nameChanged =
      first !== saved.firstName.trim() || last !== saved.lastName.trim();
    // newCompanyName is non-nullable, so an unchanged name is echoed back
    // verbatim (untrimmed): the resolver compares strictly against the stored
    // value, and a trimmed echo of a whitespace-padded name would register as
    // a change and mint a new quote.
    const companyChanged = companyName !== saved.companyName.trim();
    const mailingChanged = !addressesEqual(
      draft.mailingAddress,
      saved.mailingAddress
    );

    // Forward existing additional-insured info unchanged so it is not wiped.
    const additionalInsuredInfoListByApplicationId: AdditionalInsuredInfoListForApplication[] = Object.entries(
      additionalInsuredInfoListByAppId ?? {}
    )
      .filter(([, list]) => list != null)
      .map(([appId, additionalInsuredInfoList]) => ({
        applicationId: appId,
        additionalInsuredInfoList
      }));

    try {
      const { data } = await updateApplicationAnswersAndQuote({
        variables: {
          applicationGroupId,
          newEffectiveDate: formatISO(
            new Date(businessInfo.coverageStartDate),
            {
              representation: 'date'
            }
          ),
          newCompanyName: companyChanged ? companyName : saved.companyName,
          newDBAName: draft.dbaName.trim() || undefined,
          newEmail: draft.email.trim(),
          newPhoneNumber: draft.phoneNumber,
          ...(nameChanged ? { newFirstName: first, newLastName: last } : {}),
          ...(mailingChanged
            ? {
                newMailingAddress: {
                  street: draft.mailingAddress.street,
                  city: draft.mailingAddress.city,
                  province: draft.mailingAddress.province,
                  postalCode: draft.mailingAddress.postalCode,
                  address2: draft.mailingAddress.address2 || undefined
                }
              }
            : {}),
          additionalInsuredInfoListByApplicationId
        }
      });

      const result = data?.updateApplicationAnswersAndQuote;
      if (!result?.isUpdateSuccessful) {
        setInvalidEmail(true);
        return;
      }

      applyNewQuoteIds((result.newQuoteIds ?? []) as NewQuoteForApp[]);
      setSaved({
        ...draft,
        companyName: companyChanged ? companyName : saved.companyName
      });
      setEditing(false);
      refetchBusinessInfo?.();
    } catch {
      // Network / GraphQL failure, distinct from field-level validation.
      setSaveError(true);
    }
  };

  const locationLines = toAddressLines(businessInfo.locationAddress);

  return (
    <div className="applicant-info-container" data-testid="applicant-info">
      <div
        className={`applicant-info-header ${
          expanded ? 'is-open' : ''
        } flex justify-between items-center gap-2 bg-secondary-light px-4 md:px-8 py-4 font-bold text-lg md:text-2xl text-quartary`}
      >
        <button
          type="button"
          className="applicant-info-toggle flex items-center text-left"
          data-testid="applicant-info-toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((prev) => (editing ? true : !prev))}
        >
          <svg
            className={`applicant-info-chevron ${expanded ? 'open' : ''}`}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              d="M15 6l-6 6 6 6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="font-bold break-words md:truncate">
            Applicant Info: {saved.companyName}
          </span>
        </button>
        <div className="applicant-info-actions text-base font-normal whitespace-nowrap">
          {editing ? (
            <>
              <button
                type="button"
                className="applicant-info-link cancel mr-6"
                data-testid="applicant-info-cancel"
                onClick={cancel}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="applicant-info-link save"
                data-testid="applicant-info-save"
                onClick={save}
                disabled={loading}
              >
                {loading ? 'Saving…' : 'Save'}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="applicant-info-link save"
              data-testid="applicant-info-edit"
              onClick={startEdit}
            >
              Edit
            </button>
          )}
        </div>
      </div>

      {expanded ? (
        <div
          className="applicant-info-body bg-white px-8 py-6"
          data-testid="applicant-info-body"
        >
          {editing ? (
            <div
              className="applicant-info-edit"
              data-testid="applicant-info-edit-form"
            >
              <div className="applicant-info-field">
                <span className="applicant-info-label">Business Name:</span>
                <span className="applicant-info-value">
                  <input
                    className={`input-field w-full ${
                      invalidCompanyName ? 'invalid-row' : ''
                    }`}
                    data-testid="applicant-info-input-business-name"
                    value={draft.companyName}
                    onChange={(e) => patch({ companyName: e.target.value })}
                  />
                  {invalidCompanyName ? (
                    <span
                      className="applicant-info-error block"
                      data-testid="applicant-info-error-business-name"
                    >
                      Enter a business name.
                    </span>
                  ) : null}
                </span>
              </div>

              <div className="applicant-info-field">
                <span className="applicant-info-label">DBA:</span>
                <span className="applicant-info-value">
                  <input
                    className="input-field w-full"
                    data-testid="applicant-info-input-dba"
                    value={draft.dbaName}
                    onChange={(e) => patch({ dbaName: e.target.value })}
                  />
                </span>
              </div>

              <div className="applicant-info-field">
                <span className="applicant-info-label">
                  <span className="block">Location Address:</span>
                  <span className="applicant-info-chip">NOT EDITABLE</span>
                </span>
                <span
                  className="applicant-info-value applicant-info-readonly"
                  data-testid="applicant-info-location-readonly"
                >
                  {locationLines.map((line, i) => (
                    <span key={i} className="block">
                      {line}
                    </span>
                  ))}
                  <span className="applicant-info-note block">
                    Changing the location address requires a new application to
                    be completed.
                  </span>
                </span>
              </div>

              <div className="applicant-info-field">
                <span className="applicant-info-label">Mailing Address:</span>
                <span
                  className="applicant-info-value"
                  data-testid="applicant-info-mailing-edit"
                >
                  <Address
                    onChange={(value) => patch({ mailingAddress: value })}
                    value={draft.mailingAddress}
                    preventStateOrProvinceChange={false}
                    country={addressCountry}
                    province={province}
                    filterProvince={false}
                  />
                </span>
              </div>

              <hr className="applicant-info-divider" />

              <div className="applicant-info-field">
                <span className="applicant-info-label">Contact Name:</span>
                <span className="applicant-info-value applicant-info-name-fields">
                  <span className="applicant-info-name-col">
                    <span className="applicant-info-float">
                      <input
                        className={`input-field w-full ${
                          invalidFirstName ? 'invalid-row' : ''
                        }`}
                        data-testid="applicant-info-input-first-name"
                        // Native placeholder for the empty state (aligns with the
                        // caret exactly, like the application's name inputs); the
                        // caption label below appears once :placeholder-shown flips.
                        placeholder="First Name"
                        value={draft.firstName}
                        onChange={(e) => patch({ firstName: e.target.value })}
                      />
                      <span className="applicant-info-float-label">
                        First Name
                      </span>
                    </span>
                    {invalidFirstName ? (
                      <span
                        className="applicant-info-error block"
                        data-testid="applicant-info-error-first-name"
                      >
                        Enter a first name.
                      </span>
                    ) : null}
                  </span>
                  <span className="applicant-info-name-col">
                    <span className="applicant-info-float">
                      <input
                        className={`input-field w-full ${
                          invalidLastName ? 'invalid-row' : ''
                        }`}
                        data-testid="applicant-info-input-last-name"
                        placeholder="Last Name"
                        value={draft.lastName}
                        onChange={(e) => patch({ lastName: e.target.value })}
                      />
                      <span className="applicant-info-float-label">
                        Last Name
                      </span>
                    </span>
                    {invalidLastName ? (
                      <span
                        className="applicant-info-error block"
                        data-testid="applicant-info-error-last-name"
                      >
                        Enter a last name.
                      </span>
                    ) : null}
                  </span>
                </span>
              </div>

              <div className="applicant-info-field">
                <span className="applicant-info-label">Phone Number:</span>
                <span className="applicant-info-value">
                  <PhoneInput
                    value={draft.phoneNumber}
                    onlyCountries={['ca', 'us']}
                    country={'us'}
                    containerClass="applicant-info-phone"
                    inputClass={invalidPhone ? 'invalid-row' : ''}
                    inputProps={{ 'data-testid': 'applicant-info-input-phone' }}
                    onChange={(value) => patch({ phoneNumber: value })}
                  />
                  {invalidPhone ? (
                    <span
                      className="applicant-info-error block"
                      data-testid="applicant-info-error-phone"
                    >
                      Invalid phone number.
                    </span>
                  ) : null}
                </span>
              </div>

              <div className="applicant-info-field">
                <span className="applicant-info-label">Email Address:</span>
                <span className="applicant-info-value">
                  <input
                    className={`input-field w-full ${
                      invalidEmail ? 'invalid-row' : ''
                    }`}
                    data-testid="applicant-info-input-email"
                    value={draft.email}
                    onChange={(e) => patch({ email: e.target.value })}
                  />
                  {invalidEmail ? (
                    <span
                      className="applicant-info-error block"
                      data-testid="applicant-info-error-email"
                    >
                      Please enter a valid email.
                    </span>
                  ) : null}
                </span>
              </div>

              {saveError ? (
                <div className="applicant-info-field">
                  <span className="applicant-info-label" />
                  <span
                    className="applicant-info-error block"
                    data-testid="applicant-info-error-save"
                  >
                    Something went wrong saving your changes. Please try again.
                  </span>
                </div>
              ) : null}
            </div>
          ) : (
            <div
              className="applicant-info-view"
              data-testid="applicant-info-view"
            >
              <Row
                label="Business Name:"
                lines={[saved.companyName || '—']}
                testId="applicant-info-row-business-name"
              />
              <Row
                label="DBA:"
                lines={[saved.dbaName || '—']}
                testId="applicant-info-row-dba"
              />
              <Row
                label="Location Address:"
                lines={locationLines}
                testId="applicant-info-row-location"
              />
              <Row
                label="Mailing Address:"
                lines={toAddressLines(saved.mailingAddress)}
                testId="applicant-info-row-mailing"
              />
              <hr className="applicant-info-divider" />
              <Row
                label="Contact Name:"
                lines={[`${saved.firstName} ${saved.lastName}`.trim() || '—']}
                testId="applicant-info-row-contact"
              />
              <Row
                label="Phone Number:"
                lines={[
                  saved.phoneNumber ? formatPhoneNumber(saved.phoneNumber) : '—'
                ]}
                testId="applicant-info-row-phone"
              />
              <Row
                label="Email Address:"
                lines={[saved.email || '—']}
                testId="applicant-info-row-email"
              />
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default ApplicantInfo;
